import React, { ChangeEvent, FormEvent } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import VacancyInterface from '../../../model/Vacancy';
import UtmReader from '../UtmReader/UtmReader';
import classNames from 'classnames';
import { api } from '../../../config';
import validationSchema from './validationSchema';
import './ApplyForm.css'

import Grid from '../Grid/Grid';
// Analytics
import gtag from '../analytics/gtag.js';

// UI lib imports
import {
  DinoInputText,
  DinoInputCheckbox,
  DinoInputFile,
  DinoButton,
} from '@uchi/ui-lib-react';

interface Props {
  maxAdditionalLinks: number;
  id?: string;
  vacancy?: VacancyInterface;
  className?: string;
}

interface BasicFields {
  vacancyName: string;
  candidateName: string;
  wayToConnect: string;
}

interface AdvancedFields {
  cv: File | null;
  additionalLinks: string[];
}

type FormLoadingStatus = 'idle' | 'loading' | 'success' | 'failed';

interface State {
  fields: BasicFields & AdvancedFields;
  fieldsErrors: {
    [key: string]: string;
  },
  disabledFields: (keyof (BasicFields & AdvancedFields))[];
  permissionAdded: boolean;
  loading: {
    error: any;
    status: FormLoadingStatus;
  },
}

class ApplyForm extends React.Component<Props, State> {

  private basicFields: (keyof BasicFields)[];
  private advancedFields: (keyof AdvancedFields)[];
  private vacancy: {id: string | undefined, name: string};
  private currentClass = 'apply-form';
  private recaptchaRef: React.RefObject<ReCAPTCHA>;

  public static defaultProps = {
    maxAdditionalLinks: 3
  }

  constructor(props: Props) {
    super(props);
    const { vacancy } = this.props;
    this.vacancy = {
      id: vacancy && vacancy.id ? vacancy.id : undefined,
      name: vacancy && vacancy.name ? vacancy.name : '',
    }
    const fields = {
      vacancyName: this.vacancy.name,
      candidateName: '',
      vacancy: null,
      wayToConnect: '',
      cv: null,
      additionalLinks: [''],
    };

    this.recaptchaRef = React.createRef();
    this.state = {
      fields,
      fieldsErrors: {
        fieldName: ''
      },
      disabledFields: [],
      permissionAdded: false,
      loading: {
        error: null,
        status: 'idle',
      },
    };

    if (this.vacancy.name) this.state.disabledFields.push('vacancyName');
    this.basicFields = ['vacancyName', 'candidateName', 'wayToConnect'];
    this.advancedFields = ['cv', 'additionalLinks'];

  }

  validate = (fields: BasicFields & AdvancedFields): { [key: string]: string } => {
    try {
      validationSchema.validateSync(fields, { abortEarly: false });
      return {};
    } catch (e) {
      const innerValues = Object.values(e.inner);
      return (innerValues as any).reduce((errors: any, error: any) => {
        return {
          ...errors,
          [error.path]: error.errors[0],
        };
      }, {});
    }
  }

  /**
   * Form submission to server
   * @param  {FormEvent}  event - собитие совержащее данные формы
   * @return {Promise}                  обещание ответа сервера на обработку формы
   */
  handleSubmit = async (event: FormEvent<HTMLFormElement>, recaptchaToken: string | null): Promise<void> => {
    event.preventDefault();

    this.setState({
      loading: {
        error: null,
        status: 'loading',
      },
    });

    const cv = this.state.fields.cv;
    const { vacancy } = this.props;
    const data = {
      position: this.state.fields.vacancyName,
      fullname: this.state.fields.candidateName,
      vacancy: vacancy && vacancy.id ? vacancy.id : null,
      way_to_connect: this.state.fields.wayToConnect,
      recaptcha: recaptchaToken,
      utm: UtmReader.getStoredMetrix(),
      additional_links: this.state.fields.additionalLinks.filter((value) => value !== '').join(', '),
    };
    const formData = new FormData();

    formData.append('data', JSON.stringify(data));

    if (cv !== null) {
      formData.append('files.cv', cv, cv.name);
    }

    try {
      // GoogleAnalytics event
      this.gaEvent('submit', vacancy);

      const response = await fetch(`${api.rest}/candidates`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        // GoogleAnalytics event
        this.gaEvent('submit_error', vacancy);
        throw new Error(response.statusText);
      }

      // GoogleAnalytics event
      this.gaEvent('submit_success', vacancy);

      return this.setState({
        loading: {
          error: null,
          status: 'success',
        },
      });
    } catch (e) {
      this.setState({
        loading: {
          error: e,
          status: 'failed',
        },
      });
      Sentry.withScope((scope) => {
        scope.setExtra('data', {
          ...data,
          cv: cv === null ? null : {
            name: cv.name,
            size: cv.size,
            type: cv.type,
          },
        });
        Sentry.captureException(e);
      });
    }
  }

  handleChangeCheckbox = (event: ChangeEvent<HTMLInputElement>): void => {
    const { checked } = event.target;

    return this.setState({
      permissionAdded: checked,
    });
  }

  handleChangeFields = (): void => {
    const { fields, disabledFields } = this.state;
    const vacancyNameIndex = disabledFields.indexOf('vacancyName')
    const fieldsToValidate = Object.assign({}, fields);

    if (~vacancyNameIndex) delete fieldsToValidate.vacancyName;

    const errors = this.validate(fieldsToValidate);

    this.setState({
      fieldsErrors: errors,
    });
  }

  handleChange = (fieldKey: keyof (BasicFields & AdvancedFields), index = 0) => {
    return (event: ChangeEvent<HTMLInputElement>): void => {
      const { target } = event;
      let newFieldState;

      if (fieldKey === 'cv') {
        newFieldState = target.files ? target.files[0] : null;
      } else if (fieldKey === 'additionalLinks') {
        newFieldState = [
          ...this.state.fields[fieldKey],
        ];
        newFieldState[index] = target.value.trim();
      } else {
        newFieldState = target.value.trim();
      }

      this.setState({
        fields: {
          ...this.state.fields,
          [fieldKey]: newFieldState,
        },
      }, this.handleChangeFields);
    };
  }

  onAddLinkClick = (): void => {
    const { fields } = this.state;
    if(fields.additionalLinks.length >= this.props.maxAdditionalLinks) return;
    this.setState({
      fields: {
        ...fields,
        additionalLinks: [
          ...fields.additionalLinks,
          ''
        ],
      },
    });
  }

  handleClear = (): void => {
    this.setState({
      loading: {
        error: null,
        status: 'idle',
      },
    });
  }

  renderSuccess(): JSX.Element {
    const { className } = this.props;
    const klass = {
      [this.currentClass]: true,
      [Grid.itemClass(10, 1)]: true,
      'success': true
    };
    if (className) klass[className] = true;

    return (
      <div className={classNames(klass)}>
        <h3 className="bold">
          Поймали!<br />
          Наш человек с тобой свяжется.<br />
          Пароль: Учи.ру ;)
        </h3>
      </div>
    );
  }

  renderFailureClientErrors(): JSX.Element {
    return (
      <p className="subtitle">
        Сожалеем, но мы не смогли сохранить заявку :(.<br />Напиши нам: <a href="mailto:hr@uchi.ru">hr@uchi.ru</a>
      </p>
    );
  }

  renderFailureInternalServerError(): JSX.Element {
    return (
      <React.Fragment>
        <p className="subtitle">
          Возможно, случились проблемы с интернетом или сервер ушёл на перекур :-)<br />Попробуй еще раз или напиши нам: <a href="mailto:hr@uchi.ru">hr@uchi.ru</a>
        </p>
        <Grid>
          <div className={Grid.itemClass(4)}>
            <DinoButton type="submit" onClick={this.handleClear}>Ещё раз</DinoButton>
          </div>
        </Grid>
      </React.Fragment>
    );
  }

  renderFailure(): JSX.Element {
    const { className } = this.props;
    const { loading } = this.state;
    const klass = {
      [this.currentClass]: true,
      [Grid.itemClass(10, 1)]: true,
      'failure': true
    };
    if (className) klass[className] = true;

    return (
      <div className={classNames(klass)}>
        <div className="cross"></div>
        <h3 className="bold">Не получилось отправить форму</h3>
        {loading.error && (loading.error.status >= 400 && loading.error.status < 500)
          ? this.renderFailureClientErrors()
          : this.renderFailureInternalServerError()}
      </div>
    );
  }

  /**
   * [onRecaptchaSubmit description]
   * @param  {FormEvent}  event - form submission event to pass it in `handleSubmit`
   *                              method
   * @return {Promise} as aync function
   */
  async onRecaptchaSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    event.persist();
    try {
      if (!(this.recaptchaRef && this.recaptchaRef.current && this.recaptchaRef.current.executeAsync)) {
        throw new Error("No reCaptcha ref provided");
      }
      const token = await this.recaptchaRef.current.executeAsync();
      this.handleSubmit(event, token);
    } catch (e) {
      this.setState({
        loading: {
          error: e,
          status: 'failed',
        },
      });
      Sentry.captureException(e);
    }
  }

  /**
   * Pushes event to ga quueue
   */
  private gaEvent(action: string, vacancy?: VacancyInterface) {
    const vacancyAlias = vacancy ? vacancy.uri_alias : 'empty';
    gtag('event', action, {
      'event_category' : 'vacancy_form',
      'event_label' : `form_vacancy_${vacancyAlias}`
    });
  }

  render(): JSX.Element {
    const { fields, fieldsErrors, disabledFields, permissionAdded, loading } = this.state;

    if (loading.status === 'success') {
      return this.renderSuccess();
    } else if (loading.status === 'failed') {
      return this.renderFailure();
    }

    const { className } = this.props;
    const isLoading = loading.status === 'loading';
    const links = fields.additionalLinks.map((_, index) => {
      const value = fields.additionalLinks[index] || '';
      const error = fieldsErrors[`additionalLinks[${index}]`] ? fieldsErrors[`additionalLinks[${index}]`] : '';
      const name = 'additional-link';
      const key = `additional-link-${index}`;
      return (
        <DinoInputText
          key={key}
          type="link"
          className={Grid.itemClass(11)}
          data-name={name}
          placeholder="Ссылка"
          value={value}
          onIonChange={this.handleChange('additionalLinks', index)}
          disabled={isLoading}
          error={error}
        />
      );
    });
    const klass = {
      [this.currentClass]: true,
    };
    const windowWidth = window.innerWidth;
    let formBottomClass = Grid.itemClass(4);
    if (className) klass[className] = true;

    const basicFieldsFilled = this.basicFields.every((fieldKey) => fields[fieldKey] !== '');
    const advancedFieldsFilled = this.advancedFields.some((fieldKey) => {
      const field = fields[fieldKey];
      if (Array.isArray(field)) return field.some((item) => item !== '');
      return field !== null;
    });
    const requiredFieldsFilled = basicFieldsFilled && advancedFieldsFilled;
    const noErrorsFound = !Object.values(fieldsErrors).some((fieldError) => fieldError !== '');
    const activateSubmitButton = !isLoading && requiredFieldsFilled && noErrorsFound && permissionAdded;

    if (windowWidth > 1279) {
      formBottomClass = Grid.itemClass(6);
    }

    return (
      <form
        id={this.props.id}
        action="#"
        className={classNames(klass)}
        onSubmit={this.onRecaptchaSubmit.bind(this)}
      >

        <DinoInputText
          data-name="vacancy-name"
          placeholder="Вакансия"
          value={fields.vacancyName}
          onIonChange={this.handleChange('vacancyName')}
          disabled={isLoading || disabledFields.includes('vacancyName')}
          error={fieldsErrors.vacancyName || ''}
        />

        <DinoInputText
          type="name"
          data-name="candidate-name"
          placeholder="Фамилия и имя"
          value={fields.candidateName}
          onIonChange={this.handleChange('candidateName')}
          disabled={isLoading}
          error={fieldsErrors.candidateName || ''}
        />

        <DinoInputText
          type="text"
          data-name="way-to-connect"
          placeholder="Способ связи"
          value={fields.wayToConnect}
          // hint="Телефон, почта или ссылка на телеграм, фейсбук" Commented out due to new legislation (March 2022)
          hint="Телефон, почта или ссылка на телеграм"
          onIonChange={this.handleChange('wayToConnect')}
          disabled={isLoading}
          error={fieldsErrors.wayToConnect || ''}
        />

        <DinoInputFile
          label="Выбери файл"
          max-file-size="5500000"
          data-id="input-file"
          data-name="cv"
          hint="Или укажи ссылки ниже"
          onIonChange={this.handleChange('cv')}
          disabled={isLoading}
          error={fieldsErrors.cv || ''}
        >
          <span>&nbsp;резюме / портфолио или перетащи его сюда</span>
        </DinoInputFile>

        <input type="hidden" name="vacancy" value={ this.vacancy.id }/>

        <div className={isLoading ? 'additional-links disabled' : 'additional-links'}>
          { links }
          <div className="add-link grid-item-1" onClick={this.onAddLinkClick}><span>+ ссылка</span></div>
        </div>

        <Grid>
          <div className={formBottomClass}>
            <DinoInputCheckbox
              checked={permissionAdded}
              data-id="apply-form-checkbox"
              data-name="apply-form-checkbox"
              onIonChange={this.handleChangeCheckbox}
              disabled={isLoading}
            >
              <span>Соглашаюсь с </span>
              <a href="<%PRIVACY_POLICY_URL%>" target="_blank">обработкой персональных данных</a>
            </DinoInputCheckbox>
          </div>
          <div className={formBottomClass}>
            <DinoButton className={isLoading ? 'align-right' : 'hidden align-right'} disabled="true" type="submit">Отправка...</DinoButton>
            <DinoButton className={isLoading ? 'hidden align-right' : 'align-right'} disabled={!activateSubmitButton} type="submit">Отправить</DinoButton>
            <div style={{clear: "both", paddingTop: 10}}>
              <ReCAPTCHA
                ref={this.recaptchaRef}
                size="invisible"
                badge="inline"
                sitekey="<%GRECAPTCHA_SITEKEY%>"
              />
            </div>
          </div>
        </Grid>
      </form>
    );
  }
}

export default ApplyForm;
